import * as React from 'react';
import styled, { css, keyframes } from 'styled-components';

const marqueeLeft = keyframes`
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(-50%);
  }
`;

const marqueeRight = keyframes`
  from {
    transform: translateX(-50%);
  }
  to {
    transform: translateX(0);
  }
`;

export type MarqueeCarouselProps = {
  /** Apparent scroll direction: `left` moves content left, `right` moves content right */
  direction?: 'left' | 'right';
  /** Seconds for one full loop */
  duration?: number;
  /** Gap between slides in pixels */
  gap?: number;
  /** Pause animation on hover */
  pauseOnHover?: boolean;
  'aria-label'?: string;
  children: React.ReactNode;
};

const MarqueeCarousel: React.FC<MarqueeCarouselProps> = ({
  direction = 'left',
  duration = 40,
  gap = 24,
  pauseOnHover = true,
  'aria-label': ariaLabel = 'Carousel',
  children,
}) => {
  const slides = React.Children.toArray(children).filter(Boolean);
  const loopSlides = [...slides, ...slides];

  return (
    <Viewport role="region" aria-roledescription="carousel" aria-label={ariaLabel}>
      <Track
        $direction={direction}
        $duration={duration}
        $gap={gap}
        $pauseOnHover={pauseOnHover}
      >
        {loopSlides.map((slide, index) => (
          <Slide key={`marquee-slide-${index}`}>{slide}</Slide>
        ))}
      </Track>
    </Viewport>
  );
};

const Viewport = styled.div`
  width: 100%;
  min-width: 0;
  overflow: hidden;
`;

const Track = styled.div<{
  $direction: 'left' | 'right';
  $duration: number;
  $gap: number;
  $pauseOnHover: boolean;
}>`
  display: flex;
  width: max-content;
  gap: ${props => props.$gap}px;
  animation: ${props => (props.$direction === 'left' ? marqueeLeft : marqueeRight)}
    ${props => props.$duration}s linear infinite;
`;

const Slide = styled.div`
  flex-shrink: 0;
`;

export { MarqueeCarousel };
